import React, { useState, useRef, useEffect } from 'react';
import { Model, ModelCapability, UserPlan, ModelCategory } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { setUserPlan } from '../utils/rateLimit';
import { useTheme } from '../contexts/ThemeContext';
import { loadStripe } from '@stripe/stripe-js';
import { parseCategory, stringifyCategory } from '../models';

interface ModelSelectorProps {
    models: Model[];
    selectedModel: string;
    onSelect: (modelId: string) => void;
    userPlan: UserPlan;
    onPlanChange: () => void;
}

// Helper function to render the model icon
const renderModelIcon = (icon: React.ReactNode, isLightMode: boolean) => {
    // Check if the icon is a string starting with '/' (likely a path)
    if (typeof icon === 'string' && icon.startsWith('/logos/')) {
        // For light mode, use light version of SVG if the path doesn't already end with -light.svg
        let iconPath = icon;
        if (isLightMode && !iconPath.includes('-light.svg')) {
            // Insert '-light' before .svg
            iconPath = iconPath.replace('.svg', '-light.svg');
        }
        // It's a path, render an img tag
        // You might want to add error handling for missing images (onError prop on img)
        return <img src={iconPath} alt="Model logo" className="w-5 h-5 inline-block align-middle" />;
    } else {
        // It's likely already a ReactNode (like text 'V') or an emoji, render directly
        return <span className="text-lg inline-block align-middle">{icon}</span>;
    }
};

// Component to display capability icons
const CapabilityIcon: React.FC<{ capability: ModelCapability }> = ({ capability }) => {
    // Map of capabilities to their respective colors
    const colorMap: Record<string, string> = {
        'optimized-speed': 'text-yellow-400',
        'web-search': 'text-blue-400',
        'tool-calling': 'text-green-400',
        'vision': 'text-purple-400',
        'file-upload': 'text-orange-400',
        'reasoning': 'text-pink-400',
        'text': 'text-teal-400',
    };

    // Get the color for this capability
    const iconColor = colorMap[capability] || 'text-gray-400';

    // Tooltip content for each capability
    const tooltipContent: Record<string, string> = {
        'optimized-speed': 'Optimized for faster responses',
        'web-search': 'Can search the web for information',
        'tool-calling': 'Can use tools and APIs',
        'vision': 'Can analyze and understand images',
        'file-upload': 'Supports file uploads for analysis',
        'reasoning': 'Enhanced reasoning capabilities',
        'text': 'Text generation and conversation',
    };

    const tooltipClasses = "opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10 border border-gray-700 dark:border-gray-600 shadow-md";

    switch (capability) {
        case 'optimized-speed':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['optimized-speed']}
                    </div>
                </div>
            );
        case 'web-search':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['web-search']}
                    </div>
                </div>
            );
        case 'tool-calling':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['tool-calling']}
                    </div>
                </div>
            );
        case 'vision':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="2" />
                            <path d="M22 12c-2.667 4.667-6 7-10 7s-7.333-2.333-10-7c2.667-4.667 6-7 10-7s7.333 2.333 10 7" />
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['vision']}
                    </div>
                </div>
            );
        case 'file-upload':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['file-upload']}
                    </div>
                </div>
            );
        case 'reasoning':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-brain h-4 w-4">
                            <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"></path>
                            <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"></path>
                            <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"></path>
                            <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"></path>
                            <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path>
                            <path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path>
                            <path d="M19.938 10.5a4 4 0 0 1 .585.396"></path>
                            <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                            <path d="M19.967 17.484A4 4 0 0 1 18 18"></path>
                        </svg>

                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['reasoning']}
                    </div>
                </div>
            );
        case 'text':
            return (
                <div className="group relative">
                    <span className={`inline-flex items-center ${iconColor} hover:opacity-80 transition-opacity cursor-help`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                    </span>
                    <div className={tooltipClasses}>
                        {tooltipContent['text']}
                    </div>
                </div>
            );
        default:
            return null;
    }
};

// Enhance the ModelItem component to show family relationship
const ModelItem: React.FC<{
    model: Model,
    onClick: () => void,
    isSelected: boolean,
    userPlan: UserPlan,
    onUpgradeClick: () => void,
    isFirstInFamily?: boolean
}> = ({ model, onClick, isSelected, userPlan, onUpgradeClick, isFirstInFamily }) => {
    const { effectiveTheme } = useTheme();
    const isLightMode = effectiveTheme === 'light';
    const modelCategory = parseCategory(model.category);
    const isPlanRequired = model.planRequired && model.planRequired !== 'free';
    const isInMaintenance = model.maintenance === true;
    const isModelAvailable = !isInMaintenance && (
        !model.planRequired ||
        model.planRequired === 'free' ||
        userPlan === model.planRequired ||
        (userPlan === 'pro' && model.planRequired === 'plus') ||
        userPlan === 'maintenance'
    );

    const hasFamily = !!modelCategory.family && !modelCategory.isStandalone;

    return (
        <>
            <div
                className={`flex items-center justify-between p-2 py-2.5 rounded-md 
                    ${isModelAvailable ? 'hover:bg-background-tertiary dark:hover:bg-dark-background-tertiary cursor-pointer' : 'cursor-not-allowed opacity-70'}
                    ${isSelected ? 'bg-background-tertiary dark:bg-dark-background-tertiary' : ''}
                    ${hasFamily ? 'border-l-2 border-primary/30 dark:border-primary/40' : ''}
                    ${isFirstInFamily && hasFamily ? 'border-t border-glass-border dark:border-dark-glass-border rounded-t-md mt-1' : ''}
                `}
                onClick={isModelAvailable ? onClick : isInMaintenance ? undefined : onUpgradeClick}
            >
                <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-secondary dark:bg-dark-background-secondary mr-3">
                        {renderModelIcon(model.icon || '🤖', isLightMode)}
                    </div>
                    <div>
                        <div className="flex items-center">
                            <h3 className="font-medium text-text-primary dark:text-dark-text-primary">{model.name}</h3>
                            {model.isNew && (
                                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-green-500/20 text-green-500 rounded">
                                    NEW
                                </span>
                            )}
                            {isInMaintenance && (
                                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-amber-500/30 text-amber-600 dark:text-amber-400 rounded-md flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                        <line x1="12" y1="9" x2="12" y2="13"></line>
                                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                    </svg>
                                    MAINTENANCE
                                </span>
                            )}
                            {!isInMaintenance && isPlanRequired && userPlan !== 'maintenance' && (
                                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5">
                                    {model.planRequired === 'plus' ? (
                                        <span className="bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded px-1.5 py-0.5">PLUS</span>
                                    ) : (
                                        <span className="bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded px-1.5 py-0.5">PRO</span>
                                    )}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {isInMaintenance ? "Currently unavailable - Under maintenance" : model.description}
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {model.capabilities?.map(capability => (
                        <CapabilityIcon key={capability} capability={capability} />
                    ))}
                </div>
            </div>
            {modelCategory.showSeparator && (
                <div className="my-3 border-b-2 border-primary/30 dark:border-primary/40 mx-2"></div>
            )}
        </>
    );
};

// Add a function to format category names for display
const formatCategoryName = (category: string): string => {
    // Handle family categories (containing '/')
    if (category.includes('/')) {
        const [provider, family] = category.split('/');
        // Format family name (e.g. "GPT4.1-Family" -> "GPT-4.1 Family")
        const formattedFamily = family
            .replace(/([0-9]+)\.([0-9]+)/g, '$1.$2') // Keep decimal points
            .replace(/([A-Z0-9]+)([A-Z][a-z])/g, '$1 $2') // Add space between camelCase
            .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between camelCase
            .replace(/([a-zA-Z])([0-9])/g, '$1-$2') // Add hyphen between letter and number
            .replace(/-Family$/, ' Family'); // Replace -Family with Family

        return `${provider}/${formattedFamily}`;
    }

    return category;
};

const ModelSelector: React.FC<ModelSelectorProps> = ({
    models,
    selectedModel,
    onSelect,
    userPlan,
    onPlanChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isUpgrading, setIsUpgrading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { currentUser, isAdmin } = useAuth();
    const { effectiveTheme } = useTheme();
    const isLightMode = effectiveTheme === 'light';

    // Find the selected model details
    const selectedModelDetails = models.find((model) => model.id === selectedModel);
    const isSelectedModelInMaintenance = selectedModelDetails?.maintenance === true;

    // Group models by category
    const groupedModels = models.reduce((groups, model) => {
        // Get category string from category object or string
        const categoryStr = stringifyCategory(model.category || 'Other');

        if (!groups[categoryStr]) {
            groups[categoryStr] = [];
        }
        groups[categoryStr].push(model);
        return groups;
    }, {} as Record<string, Model[]>);

    // Sort categories to ensure "Pinned" comes first, then alphabetical
    const sortedCategories = Object.keys(groupedModels).sort((a, b) => {
        if (a === 'Pinned') return -1;
        if (b === 'Pinned') return 1;
        return a.localeCompare(b);
    });

    // Filter models based on search term
    const filteredGroups = searchTerm.trim() === ''
        ? groupedModels
        : Object.keys(groupedModels).reduce((acc, category) => {
            const filteredModels = groupedModels[category].filter(model =>
                model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                model.description.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (filteredModels.length > 0) {
                acc[category] = filteredModels;
            }

            return acc;
        }, {} as Record<string, Model[]>);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleUpgrade = async () => {
        if (!currentUser?.uid) return;
        setIsUpgrading(true);
        try {
            // Create Stripe checkout session for payment
            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    returnUrl: window.location.origin,
                }),
            });

            if (!response.ok) throw new Error('Failed to create checkout session');

            const { sessionId } = await response.json();

            // Load Stripe and redirect to checkout
            const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
            if (!stripe) throw new Error('Failed to load Stripe');

            await stripe.redirectToCheckout({ sessionId });
        } catch (error) {
            console.error("Checkout failed:", error);
            alert("Failed to start checkout process. Please try again.");
        } finally {
            setIsUpgrading(false);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border px-3 py-2 rounded-full text-sm font-medium transition-all hover:border-glass-shine dark:hover:border-dark-glass-shine hover:shadow-glow dark:hover:shadow-dark-glow text-text-primary dark:text-dark-text-primary"
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <span className="flex items-center">
                    {selectedModelDetails?.icon && (
                        <span className="mr-2">{renderModelIcon(selectedModelDetails.icon, isLightMode)}</span>
                    )}
                    <span className="flex items-center">
                        {selectedModelDetails?.name || 'Select Model'}
                        {isSelectedModelInMaintenance && (
                            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 bg-amber-500/30 text-amber-600 dark:text-amber-400 rounded-md flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                    <line x1="12" y1="9" x2="12" y2="13"></line>
                                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                </svg>
                                MAINTENANCE
                            </span>
                        )}
                    </span>
                </span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 right-0 bg-background-primary dark:bg-dark-background-primary border border-glass-border dark:border-dark-glass-border rounded-xl shadow-glass dark:shadow-dark-glass backdrop-blur-sm w-[500px] max-h-[90vh] overflow-hidden z-50 animate-fadeIn">
                    <div className="p-3 border-b border-glass-border dark:border-dark-glass-border sticky top-0 z-10 bg-background-primary dark:bg-dark-background-primary backdrop-blur-sm">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search models..."
                                className="w-full bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md py-2 pl-8 pr-3 text-sm text-text-primary dark:text-dark-text-primary focus:outline-none focus:border-primary dark:focus:border-dark-primary focus:ring-1 focus:ring-primary dark:focus:ring-dark-primary"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400 dark:text-gray-500"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                        </div>

                        {userPlan === 'free' && (
                            <div className="mt-2 flex justify-between items-center">
                                <h3 className="text-sm text-gray-600 dark:text-gray-300">Unlock all models + higher limits</h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl font-bold text-fuchsia-500">Free</span>
                                    <button
                                        onClick={handleUpgrade}
                                        disabled={isUpgrading}
                                        className={`bg-fuchsia-700 hover:bg-fuchsia-600 text-white text-xs font-medium py-1 px-3 rounded-md transition-colors ${isUpgrading ? 'opacity-50 cursor-wait' : ''}`}
                                    >
                                        {isUpgrading ? 'Upgrading...' : 'Upgrade Now'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto">
                        {Object.keys(filteredGroups).length > 0 ? (
                            sortedCategories
                                .filter(category => filteredGroups[category])
                                .map(category => (
                                    <div key={category} className="mb-4">
                                        <h3 className="text-sm font-semibold px-3 py-1.5 text-primary dark:text-dark-primary bg-background-secondary/40 dark:bg-dark-background-secondary/60 border-l-4 border-primary dark:border-dark-primary">
                                            {category.replace('/', ' / ').replace('-Family', ' Family')}
                                        </h3>
                                        <div className="bg-background-primary/70 dark:bg-dark-background-primary/70 backdrop-blur-sm rounded-md overflow-hidden">
                                            {filteredGroups[category].map((model, index) => {
                                                // Determine if this is the first model in its family
                                                const modelCategory = parseCategory(model.category);
                                                const prevModelCategory = index > 0 ? parseCategory(filteredGroups[category][index - 1].category) : null;
                                                const isFirstInFamily = !!(
                                                    modelCategory.family &&
                                                    (!prevModelCategory?.family || prevModelCategory.family !== modelCategory.family)
                                                );

                                                return (
                                                    <ModelItem
                                                        key={model.id}
                                                        model={model}
                                                        onClick={() => {
                                                            onSelect(model.id);
                                                            setIsOpen(false);
                                                        }}
                                                        isSelected={selectedModel === model.id}
                                                        userPlan={userPlan}
                                                        onUpgradeClick={handleUpgrade}
                                                        isFirstInFamily={isFirstInFamily}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                        ) : (
                            <div className="p-2 text-center text-gray-500 dark:text-gray-400">
                                No models found matching "{searchTerm}"
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelSelector; 